import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { ArrowRight } from "lucide-react";

const ADMIN_BG = "https://static.prod-images.emergentagent.com/jobs/d0292276-e84a-42f6-aad0-4f23c6fd8a65/images/33ee8b78508b774912d30f96afd827435721ff9ac96dddf9085b4737b10e539f.png";

export default function AdminLogin() {
  const { settings } = useSettings();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("vault_token", data.access_token);
      localStorage.setItem("vault_email", data.email);
      toast.success("Welcome back");
      navigate("/admin/overview");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex" data-testid="admin-login-page">
      {/* Left graphic */}
      <div
        className="hidden lg:block w-1/2 relative"
        style={{
          backgroundImage: `linear-gradient(rgba(10,10,12,0.55), rgba(10,10,12,0.92)), url(${ADMIN_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <img src={settings.logo_url} alt={settings.brand_name} className="w-10 h-10 object-contain" style={{ mixBlendMode: 'lighten' }} />
            <span className="font-display text-xl">{settings.brand_name}</span>
          </div>
          <div>
            <p className="overline text-[var(--vault-text-muted)] mb-4">Cellar Console</p>
            <h2 className="font-display text-5xl leading-tight max-w-md">
              {(settings.tagline || "").split(/(?<=\.)\s+/).map((part, i, arr) => (
                <React.Fragment key={`${part}-${i}`}>
                  {part}{i < arr.length - 1 && <br />}
                </React.Fragment>
              ))}
            </h2>
          </div>
          <p className="overline text-[var(--vault-text-faint)]">Authorized personnel only</p>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center px-6 sm:px-10 py-12" style={{ background: "var(--vault-bg)" }}>
        <div className="w-full max-w-md fade-up">
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-6 lg:hidden">
              <img src={settings.logo_url} alt={settings.brand_name} className="w-10 h-10 object-contain" style={{ mixBlendMode: 'lighten' }} />
              <span className="font-display text-xl">{settings.brand_name}</span>
            </div>
            <p className="overline text-[var(--vault-text-muted)] mb-3">Sign in</p>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight">Cellar Console</h1>
            <p className="text-[var(--vault-text-muted)] mt-3">Manage codes, upload certificates, monitor access.</p>
          </div>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="overline block text-[var(--vault-text-muted)] mb-2">Email</label>
              <input
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-md text-white outline-none transition-colors"
                style={{ background: "var(--vault-surface)", border: "1px solid var(--vault-border)" }}
                onFocus={(e) => (e.currentTarget.style.borderColor = "var(--vault-gold)")}
                onBlur={(e) => (e.currentTarget.style.borderColor = "var(--vault-border)")}
                placeholder="admin@vault.app"
              />
            </div>
            <div>
              <label className="overline block text-[var(--vault-text-muted)] mb-2">Password</label>
              <input
                data-testid="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-md text-white outline-none transition-colors"
                style={{ background: "var(--vault-surface)", border: "1px solid var(--vault-border)" }}
                onFocus={(e) => (e.currentTarget.style.borderColor = "var(--vault-gold)")}
                onBlur={(e) => (e.currentTarget.style.borderColor = "var(--vault-border)")}
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="btn-gold w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md"
            >
              {loading ? <span className="spin" /> : (
                <>
                  <span>Enter Cellar</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          <a
            href="/"
            data-testid="back-to-portal"
            className="block mt-8 text-center overline text-[var(--vault-text-muted)] hover:text-white transition-colors"
          >
            ← Back to Portal
          </a>
        </div>
      </div>
    </div>
  );
}
